import * as React from 'react'

import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { debounceTimeout } from './debounceTimeout'
import { ExecutionElement } from '../flow/ExecutionElement'
import { ElementLabel } from './ElementLabel'

import type * as Flows from '../../../shared/domain/flows/types.no-deps'
import type { CreateAnswer } from '../../../shared/domain/flows/answer.no-deps'
import type { TimelineDetails } from './TimelineDetails'
import type { LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'

interface Props {
    deps: CommonDeps
    elementId: Flows.ElementId
    loopPathIds: LoopPathIds
    label: string
    timelineDetails: TimelineDetails
    required: boolean
    multipleLines: boolean
    answerValue: Flows.TextCaptureAnswer['value']
    validationMessages: string[]
    createAnswer: CreateAnswer<Flows.TextCaptureAnswer>
    openOptions?: () => void
}

interface State {
    editValue?: string
}

export class ElementTextCapture extends React.Component<Props, State> {
    public render = () => renderCatch(this.props.deps, this.tryRender)

    public state: State = {}

    private element: HTMLInputElement | HTMLTextAreaElement
    private timeout: number

    private onInput = () => {
        this.setState({ editValue: this.element.value })
        clearTimeout(this.timeout)
        this.timeout = window.setTimeout(this.createAnswer, debounceTimeout)
    }

    private onFocus = () => {
        this.setState({ editValue: this.props.answerValue ?? '' })
    }

    private onBlur = async () => {
        clearTimeout(this.timeout)
        await this.createAnswer()
        this.setState({ editValue: null })
    }

    private createAnswer = async () => {
        const currentValue = this.props.answerValue ?? ''
        const nextValue = this.element.value.trim()
        if (currentValue === nextValue) return

        await this.props.createAnswer((base) => ({
            ...base,
            type: 'text-capture',
            value: nextValue,
        }))
    }

    private tryRender = () => {
        const { deps, elementId, label, timelineDetails, required, validationMessages } = this.props

        const value = this.state.editValue ?? this.props.answerValue ?? ''

        return (
            <ExecutionElement
                deps={deps}
                elementId={elementId}
                loopPathIds={this.props.loopPathIds}
                validationMessages={validationMessages}
                timelineDetails={timelineDetails}
            >
                <ElementLabel
                    required={required}
                    label={label}
                    hasBeenEdited={timelineDetails && timelineDetails.hasBeenEdited}
                    language={deps.language}
                    validationMessages={validationMessages}
                    openOptions={this.props.openOptions}
                />
                <div className="AppCore__flow__ElementTextCapture">
                    {this.props.multipleLines ? (
                        <textarea
                            ref={(ref) => (this.element = ref)}
                            className="AppCore__flow__ElementTextCapture__textarea focusable"
                            data-testid="action-comment-textarea"
                            id={`AppCore__flow__ElementTextCapture__textarea___${this.props.elementId}`}
                            value={value}
                            onChange={this.onInput}
                            onFocus={this.onFocus}
                            onBlur={this.onBlur}
                            rows={15}
                            tabIndex={-1}
                        />
                    ) : (
                        <input
                            ref={(ref) => (this.element = ref)}
                            type="text"
                            className="AppCore__flow__ElementTextCapture__input focusable"
                            data-testid="flow-execution-text-capture-input"
                            value={value}
                            onChange={this.onInput}
                            onFocus={this.onFocus}
                            onBlur={this.onBlur}
                            tabIndex={-1}
                        />
                    )}
                </div>
            </ExecutionElement>
        )
    }
}
